import { useEffect, useRef, useState } from "react";

import { TierBadge } from "../../../components/ui/tierBadge";
import DealerInfoModal from "./DealerInfoModal";
import type { DealIntelligenceCardViewModel, SignerSummary } from "./DealIntelligenceCard.types";

/** Join the two address lines, skipping an empty second line (no dangling ", "). */
const formatAddress = (signer: SignerSummary): string =>
  [signer.addressLine1, signer.addressLine2].filter(Boolean).join(", ");

// ---------------------------------------------------------------------------
// useIsNarrowContainer — measures one element via ResizeObserver and reports
// whether its content width is below `thresholdPx`. A single boolean drives a
// single conditional render (exactly one layout in the DOM at a time) — unlike
// a CSS container-query toggle, which keeps both layouts in the DOM and can
// leave both visible if the container's containment box doesn't compute the
// way the query expects.
// ---------------------------------------------------------------------------
function useIsNarrowContainer(thresholdPx: number) {
  const sectionReference = useRef<HTMLElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const element = sectionReference.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setIsNarrow(entry.contentRect.width < thresholdPx);
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [thresholdPx]);

  return { sectionReference, isNarrow };
}

// ---------------------------------------------------------------------------
// InlineMetric — an inline "Label: value" pair as a <dl>. Used for Dealer and
// Advance.
// ---------------------------------------------------------------------------
interface InlineMetricProperties {
  label: string;
  value: string;
}

function InlineMetric({ label, value }: InlineMetricProperties) {
  return (
    <dl className="flex items-baseline gap-1 text-xs">
      <dt className="text-primary-foreground opacity-70">{label}:</dt>
      <dd className="text-primary-foreground font-bold">{value}</dd>
    </dl>
  );
}

// ---------------------------------------------------------------------------
// DealerMetric — same "Label: value" shape as InlineMetric, but the value is a
// button opening the Dealer Information modal. Renders as plain text (not a
// button) when there's no dealer name to show.
// ---------------------------------------------------------------------------
function DealerMetric({ value, onOpen }: { value: string; onOpen: () => void }) {
  return (
    <dl className="flex items-baseline gap-1 text-xs">
      <dt className="text-primary-foreground opacity-70">Dealer:</dt>
      <dd className="text-primary-foreground font-bold">
        {value ? (
          <button
            type="button"
            onClick={onOpen}
            className="underline decoration-dotted underline-offset-2 hover:opacity-80"
          >
            {value}
          </button>
        ) : (
          value
        )}
      </dd>
    </dl>
  );
}

// ---------------------------------------------------------------------------
// SignerInlineRow — one signer as a table row. The row wrapper uses
// `display: contents` (Tailwind `contents`) so its cells participate directly
// in the parent grid's column tracks (keeping alignment) while still exposing
// role="row" → role="cell" structure to assistive tech.
// ---------------------------------------------------------------------------
function SignerInlineRow({ signer }: { signer: SignerSummary }) {
  return (
    <div role="row" className="contents">
      <span role="cell" className="text-primary-foreground whitespace-nowrap font-bold">
        {signer.name}
      </span>
      <span role="cell" className="text-primary-foreground whitespace-nowrap font-bold">
        {signer.ssn}
      </span>
      <span role="cell" className="text-primary-foreground whitespace-nowrap font-bold">
        {signer.dob}
      </span>
      <span role="cell" className="text-primary-foreground font-bold">
        {formatAddress(signer)}
      </span>
      <span role="cell" className="text-primary-foreground whitespace-nowrap font-bold">
        {signer.phone}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SignerInlineGrid — signers as a single aligned table. A CSS grid whose
// header + signer rows share the same column tracks so they line up. Name,
// SSN, DOB and Phone are content-sized; Address takes the remaining space and
// is the only track allowed to shrink/wrap (minmax), so the row never overflows
// its container. max-content tracks are not expressible with the standard
// Tailwind grid tokens (which use 1fr), hence the inline gridTemplateColumns.
//
// ARIA table roles (table/row/columnheader/cell) give the visual grid the same
// label→value association the card fallback gets from dl/dt/dd; the row
// wrappers use `display: contents` so they don't disturb the grid layout.
// ---------------------------------------------------------------------------
function SignerInlineGrid({ driver, secondSigner }: { driver: SignerSummary; secondSigner?: SignerSummary }) {
  return (
    <div
      role="table"
      className="grid items-baseline gap-x-4 gap-y-1 text-xs"
      style={{
        gridTemplateColumns: "max-content max-content max-content minmax(0, max-content) max-content",
      }}
    >
      {/* Header row — labels in normal case; SSN/DOB are uppercase by content. */}
      <div role="row" className="contents">
        <span role="columnheader" className="text-primary-foreground opacity-70">
          Name
        </span>
        <span role="columnheader" className="text-primary-foreground opacity-70">
          SSN
        </span>
        <span role="columnheader" className="text-primary-foreground opacity-70">
          DOB
        </span>
        <span role="columnheader" className="text-primary-foreground opacity-70">
          Address
        </span>
        <span role="columnheader" className="text-primary-foreground opacity-70">
          Phone
        </span>
      </div>

      <SignerInlineRow signer={driver} />
      {secondSigner && <SignerInlineRow signer={secondSigner} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SignerSummaryCard — one signer's details stacked vertically (name, SSN, DOB,
// address, phone). Used as a column in the narrow-container stacked layout.
// ---------------------------------------------------------------------------
interface SignerSummaryCardProperties {
  /** Display label for the signer (e.g. "Driver", "2nd Signer"). Not an ARIA role. */
  signerLabel: string;
  signer: SignerSummary;
}

function SignerSummaryCard({ signerLabel, signer }: SignerSummaryCardProperties) {
  return (
    <div className="flex flex-col gap-0.5 text-center text-xs">
      <p className="text-primary-foreground font-semibold opacity-70">{signerLabel}</p>
      {/* Values stacked one per line; labels are screen-reader-only. */}
      <dl className="flex flex-col gap-0.5">
        <div>
          <dt className="sr-only">Name</dt>
          <dd className="text-primary-foreground font-bold">{signer.name}</dd>
        </div>
        <div>
          <dt className="sr-only">SSN</dt>
          <dd className="text-primary-foreground font-bold">{signer.ssn}</dd>
        </div>
        <div>
          <dt className="sr-only">DOB</dt>
          <dd className="text-primary-foreground font-bold">{signer.dob}</dd>
        </div>
        <div>
          <dt className="sr-only">Address</dt>
          <dd className="text-primary-foreground font-bold">{formatAddress(signer)}</dd>
        </div>
        <div>
          <dt className="sr-only">Phone</dt>
          <dd className="text-primary-foreground font-bold">{signer.phone}</dd>
        </div>
      </dl>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SignerDetails — space-driven signer layout, single render. Measures its own
// width (useIsNarrowContainer) and renders exactly one layout: the aligned
// inline row once there's room (>= 672px, matching the old @2xl threshold),
// else the 2-column stacked fallback. Never both.
// ---------------------------------------------------------------------------
function SignerDetails({ driver, secondSigner }: { driver: SignerSummary; secondSigner?: SignerSummary }) {
  const { sectionReference, isNarrow } = useIsNarrowContainer(672);

  return (
    <section aria-label="Signer details" ref={sectionReference}>
      {isNarrow ? (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          <SignerSummaryCard signerLabel="Driver" signer={driver} />
          {secondSigner && <SignerSummaryCard signerLabel="2nd Signer" signer={secondSigner} />}
        </div>
      ) : (
        <SignerInlineGrid driver={driver} secondSigner={secondSigner} />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// DealIntelligenceCard — sticky navy header card for the Review screen.
//
// Layout is space-driven via CSS container queries (the header is the query
// container); no JS measurement:
//
//   @5xl+ (two columns)  — Left: approval/tier/status + signer details.
//                          Right: Dealer + Advance.
//   below @5xl (stacked) — Row 1: Approval # + tier (grouped) · Status · Dealer
//                          (Dealer here only at @3xl). Row 2: Advance (+ Dealer
//                          when it did not fit row 1, i.e. below @3xl). Then the
//                          signer details.
//
// Signer details (SignerDetails) measure their own width and render exactly
// one layout: the aligned inline row, or the 2-column stack once narrow.
//
// Dealer/Advance are rendered in more than one slot; CSS reveals exactly one of
// each, so a hidden slot is display:none (absent from the accessibility tree).
//
// Data: purely presentational; receives the full view model via props.
// Real BFF wiring is deferred (OR-22250 follow-up).
// ---------------------------------------------------------------------------
interface DealIntelligenceCardProperties {
  data: DealIntelligenceCardViewModel;
}

function DealIntelligenceCard({ data }: DealIntelligenceCardProperties) {
  const { approvalId, tier, status, driver, secondSigner, dealerName, advance } = data;
  const [isDealerInfoOpen, setIsDealerInfoOpen] = useState(false);

  return (
    // A labelled <section> (role=region) rather than <header>: this card is
    // nested inside <main>, where a <header> would not map to a banner landmark;
    // TopBar is the page's banner. A region is a valid landmark anywhere.
    <section
      aria-label="Deal Intelligence"
      className="bg-primary text-primary-foreground @container sticky top-0 z-10 px-3 py-2 lg:px-4 lg:py-3"
    >
      {/* Two-column layout at @5xl (left: approval + signers · right: dealer +
          advance); single stacked column below that. */}
      <div className="@5xl:grid @5xl:grid-cols-3 @5xl:items-start @5xl:gap-x-6 flex flex-col gap-2">
        {/* Left region — full width below @5xl, 2/3 at @5xl+. */}
        <div className="@5xl:col-span-2 flex flex-col gap-2">
          {/* Row 1: Approval # + tier (grouped) · Status · Dealer.
              Approval # + tier stay together so Status wraps as a whole; Dealer
              shows here only at medium widths (@3xl) — below that it drops to
              row 2, and at @5xl it moves to the right column. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="flex items-center gap-2">
              <span className="opacity-80">Approval #</span>
              <span className="text-sm font-semibold">{approvalId}</span>
              {/* Per-tier badge; the tier name is the accessible label (not
                  color alone). STANDARD renders nothing. */}
              <TierBadge tier={tier} />
            </span>

            <span>
              Status: <span className="font-semibold">{status}</span>
            </span>

            <div className="@3xl:block @5xl:hidden hidden">
              <DealerMetric value={dealerName} onOpen={() => setIsDealerInfoOpen(true)} />
            </div>
          </div>

          {/* Row 2: Advance (+ Dealer when it did not fit row 1). Hidden in the
              two-column layout, where Advance/Dealer live in the right column. */}
          <div className="@5xl:hidden flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <InlineMetric label="Advance" value={advance} />
            <div className="@3xl:hidden">
              <DealerMetric value={dealerName} onOpen={() => setIsDealerInfoOpen(true)} />
            </div>
          </div>

          {/* Signer details (inline aligned row, or 2-column fallback). */}
          <SignerDetails driver={driver} secondSigner={secondSigner} />
        </div>

        {/* Right region — Dealer + Advance, only in the two-column layout (@5xl+). */}
        <div className="@5xl:col-span-1 @5xl:flex @5xl:flex-col hidden gap-1">
          <DealerMetric value={dealerName} onOpen={() => setIsDealerInfoOpen(true)} />
          <InlineMetric label="Advance" value={advance} />
        </div>
      </div>

      <DealerInfoModal open={isDealerInfoOpen} onOpenChange={setIsDealerInfoOpen} approvalId={approvalId} />
    </section>
  );
}

export default DealIntelligenceCard;
